import React, { useState, useEffect, useCallback } from "react";

/**
 * ImpressionsPanel — the agency-facing read view of DSP "Impressions".
 *
 * The consuming counterpart to ImpressionsSession: DSPs record reflections after
 * watching a client's story stream; a coordinator (or admin/owner) reads them
 * here. CAP_EDIT-gated server-side (list_engagements) — a DSP can't read other
 * people's reflections, only their own.
 *
 * Two levels: a summary list (who reflected, when, how they entered, status),
 * and an expand-to-read detail (the assembled summary + the full Q&A), fetched
 * lazily per row so opening the panel is cheap.
 */

const ENTRY_LABEL = {
  stream_complete:  "after finishing the stream",
  stream_abandoned: "after leaving partway",
  persistent:       "on their own",
};

const STATUS_CONFIG = {
  completed:    { label: "Complete",   color: "var(--color-status-live)",   bg: "var(--color-accent-subtle)" },
  in_progress:  { label: "Started",    color: "var(--color-status-draft)",  bg: "#fef3e2" },
  exited_early: { label: "Left early", color: "var(--color-text-tertiary)", bg: "var(--color-bg-subtle)" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function ImpressionsPanel({ clientId, clientName }) {
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [openId, setOpenId]   = useState(null);
  const [details, setDetails] = useState({});   // id -> session detail
  const [detailBusy, setDetailBusy] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/engagements?purpose=app_eval`);
      if (!r.ok) throw new Error("Couldn't load impressions.");
      setRows(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  async function toggle(row) {
    if (openId === row.id) { setOpenId(null); return; }
    setOpenId(row.id);
    if (!details[row.id]) {
      setDetailBusy(row.id);
      try {
        const r = await fetch(`/api/clients/${clientId}/engagements/${row.id}`);
        if (!r.ok) throw new Error("Couldn't load that reflection.");
        const d = await r.json();
        setDetails(prev => ({ ...prev, [row.id]: d.session }));
      } catch (e) {
        setDetails(prev => ({ ...prev, [row.id]: { _error: e.message } }));
      } finally { setDetailBusy(null); }
    }
  }

  async function remove(row) {
    if (!window.confirm("Delete this impression? It leaves this list; nothing else changes.")) return;
    try {
      const r = await fetch(`/api/clients/${clientId}/engagements/${row.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Couldn't delete that.");
      setRows(prev => prev.filter(x => x.id !== row.id));
    } catch (e) { setError(e.message); }
  }

  return (
    <div style={s.body}>
      <p style={s.sub}>
        What support providers noticed after meeting {clientName || "this client"} through their story.
      </p>

      {loading && <p style={s.muted}>Loading…</p>}
      {error && <p style={s.error}>{error}</p>}

      {rows && rows.length === 0 && !loading && (
        <p style={s.empty}>
          No impressions yet. They'll appear here once a support provider watches
          this client's story and shares a reflection.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div style={s.list}>
          {rows.map(row => {
            const st = STATUS_CONFIG[row.status] || STATUS_CONFIG.in_progress;
            const isOpen = openId === row.id;
            const detail = details[row.id];
            return (
              <div key={row.id} style={s.row}>
                <button style={s.rowHead} onClick={() => toggle(row)}>
                  <div style={s.rowMain}>
                    <span style={s.rowWho}>{row.viewer_name || "A support provider"}</span>
                    <span style={s.rowMeta}>
                      {fmtDate(row.completed_at || row.created_at)}
                      {row.entry_context && ENTRY_LABEL[row.entry_context]
                        ? ` · ${ENTRY_LABEL[row.entry_context]}` : ""}
                    </span>
                  </div>
                  <span style={{ ...s.badge, color: st.color, background: st.bg }}>
                    {row.is_empty ? "Empty" : st.label}
                  </span>
                  <span style={s.chev}>{isOpen ? "▲" : "▼"}</span>
                </button>
                <button type="button" onClick={() => remove(row)} aria-label="Delete impression"
                        title="Delete" style={s.del}>×</button>

                {isOpen && (
                  <div style={s.detail}>
                    {detailBusy === row.id && <p style={s.muted}>Loading…</p>}
                    {detail?._error && <p style={s.error}>{detail._error}</p>}
                    {detail && !detail._error && (
                      <>
                        {detail.final_text && (
                          <div style={s.summaryBlock}>
                            <div style={s.summaryLabel}>Summary</div>
                            <p style={s.summaryText}>{detail.final_text}</p>
                          </div>
                        )}
                        {Array.isArray(detail.conversation) && detail.conversation.length > 0 && (
                          <div style={s.convo}>
                            <div style={s.summaryLabel}>What they said</div>
                            {detail.conversation.map((turn, i) => (
                              <div key={i} style={s.turn}>
                                {turn.asked_question && <p style={s.q}>{turn.asked_question}</p>}
                                {turn.answer && <p style={s.a}>{turn.answer}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                        {!detail.final_text &&
                         !(detail.conversation && detail.conversation.length) && (
                          <p style={s.muted}>This reflection has no recorded content yet.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  body:        { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  del:         { position: "absolute", top: 6, right: 6, width: 26, height: 26, borderRadius: 999, border: "none", background: "none", color: "var(--color-text-tertiary)", fontSize: 16, cursor: "pointer", lineHeight: 1 },
  head:        { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:       { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  count:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  sub:         { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, marginBottom: "var(--space-2)" },
  muted:       { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: "var(--space-2) 0" },
  empty:       { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", lineHeight: "var(--leading-normal)", margin: 0 },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: "var(--space-2) 0" },
  list:        { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  row:         { border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", overflow: "hidden", position: "relative" },
  rowHead:     { width: "100%", display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3) 36px var(--space-3) var(--space-3)", background: "none", border: "none", cursor: "pointer", font: "inherit", textAlign: "left" },
  rowMain:     { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  rowWho:      { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  rowMeta:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  badge:       { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" },
  chev:        { fontSize: 10, color: "var(--color-text-tertiary)" },
  detail:      { padding: "var(--space-4)", borderTop: "1px solid var(--color-border)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  summaryBlock:{ display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  summaryLabel:{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)" },
  summaryText: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)", margin: 0 },
  convo:       { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  turn:        { display: "flex", flexDirection: "column", gap: 2 },
  q:           { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", margin: 0 },
  a:           { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)", margin: 0, paddingLeft: "var(--space-3)", borderLeft: "2px solid var(--color-accent-subtle)" },
};
